import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { AuthService } from 'app/core/authentication/services/auth.service';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { UserDetailedInterface, UserInterface } from 'app/core/user/models';
import { UserRestConnectorService, UserService } from 'app/core/user/services';
import { SelectUser, SelectUserCompleted } from 'app/core/user/state/actions/user-select.action';
import { empty, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { ErgoTeamMemberInterface } from '../models/ergo-team-member.model';

import {
  LoadAdminUsersCompleted,
  LoadDetailedUsersCompleted,
  LoadUsersCompleted,
  UpdateUser,
  UserActions,
  UserActionType,
  UserDetailRequest,
  UserDetailRequestRejected,
  UserUpdated,
} from './actions';
import { AddErgoTeamMember, CreateUser } from './actions/user-create.action';
import { DeleteUser, DeleteUserCompleted, RemoveErgoTeamMember } from './actions/user-delete.action';
import {
  LoadSingleUser,
  LoadSingleUserCompleted,
  LoadUserErgoTeam,
} from './actions/user-load.action';

@Injectable()
export class UserEffects {
  public source: string = 'UserEffects';

  @Effect()
  public loadSingleUser$: Observable<Action> = this._actions$
    .pipe(
      ofType(UserActionType.LoadSingleUser),
      map((action: LoadSingleUser) => action.payload),
      switchMap((userId: number) => {
        return this._userRestConnectorService.loadOne(userId).pipe(
          map((user: UserInterface) => new LoadSingleUserCompleted(user)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadUsers$: Observable<Action> = this._actions$
    .pipe(
      ofType(UserActionType.LoadUsers),
      switchMap(() => {
        return this._userRestConnectorService.loadAll().pipe(
          map((users: UserInterface[]) => new LoadUsersCompleted(users)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadDetailedUsers$: Observable<Action> = this._actions$
    .pipe(
      ofType(UserActionType.LoadDetailedUsers),
      switchMap(() => {
        return this._userRestConnectorService.loadAllDetailed().pipe(
          map((users: UserDetailedInterface[]) => new LoadDetailedUsersCompleted(users)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadAdminUsers$: Observable<Action> = this._actions$
    .pipe(
      ofType(UserActionType.LoadAdminUsers),
      switchMap(() => {
        return this._userRestConnectorService.loadAllAdmin().pipe(
          map((users: UserInterface[]) => new LoadAdminUsersCompleted(users)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public loadUserFromLoggedInUser$: Observable<Action> = this._actions$
    .pipe(
      ofType(UserActionType.LoadUserFromLoggedInUser),
      switchMap(() => {
        return this._authService.getLoggedInUser().pipe(
          take(1),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
      switchMap((user: UserInterface) => {
        if (typeof user === 'undefined' || !user || !user.id) {
          return empty();
        }
        return this._userRestConnectorService.loadOne(user.id).pipe(
          switchMap((user: UserInterface) => {
            const users: UserInterface[] = [];
            if (!user  || !user.id) {
              return [];
            }
            users.push(user);
            return [
              new LoadUsersCompleted(users),
            ];
          }),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.noSnackbar,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public userDetail$: Observable<Action> = this._actions$
    .pipe(
      ofType(UserActionType.UserDetailRequest),
      map((action: UserDetailRequest) => action.payload),
      switchMap((userId: number) => {
        return this._userService.findOne(userId).pipe(
          map(this.handleUserDetailResponse),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateUser$: Observable<Action> = this._actions$
  .pipe(
    ofType(UserActionType.UserUpdate),
    map((action: UpdateUser) => action.payload),
    switchMap((user: UserInterface) => {
      return this._userRestConnectorService.update(user).pipe(
        switchMap((user: UserInterface) => [new LoadSingleUserCompleted(user)]),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  @Effect()
  public createUser$: Observable<Action> = this._actions$
  .pipe(
    ofType(UserActionType.CreateUser),
    map((action: CreateUser) => action.payload),
    switchMap((user: UserInterface) => {
      return this._userRestConnectorService.createNewUser(user).pipe(
        switchMap((user: UserInterface) => [new LoadSingleUserCompleted(user)]),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  @Effect()
  public selectUser$: Observable<Action> = this._actions$
    .pipe(
      ofType(UserActionType.SelectUser),
      map((action: SelectUser) => new SelectUserCompleted(action.payload)),
      catchError((err: any) => of(createErrorRaised(new Error({
        message: ErrorMessage.unexpected,
        errorResponse: err,
        source: this.source,
      })))));

  @Effect()
  public loadUserErgoTeam$: Observable<Action> = this._actions$
  .pipe(
    ofType(UserActionType.LoadUserErgoTeam),
    map((action: LoadUserErgoTeam) => action.payload),
    switchMap((user: UserInterface) => {
      return this._userRestConnectorService.loadUserErgoTeam(user).pipe(
        map((userWithTeam: UserInterface) => new LoadSingleUserCompleted(userWithTeam)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public addErgoTeamMember$: Observable<Action> = this._actions$
  .pipe(
    ofType(UserActionType.AddErgoTeamMember),
    map((action: AddErgoTeamMember) => action.payload),
    switchMap((payloadData: { user: UserInterface, teamMember: ErgoTeamMemberInterface }) => {
      return this._userRestConnectorService.addUserErgoTeamMember(payloadData.user, payloadData.teamMember)
      .pipe(
        map((updatedUser: UserInterface) => new LoadSingleUserCompleted(updatedUser)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public removeErgoTeamMember$: Observable<Action> = this._actions$
  .pipe(
    ofType(UserActionType.RemoveErgoTeamMember),
    map((action: RemoveErgoTeamMember) => action.payload),
    switchMap((payloadData: { user: UserInterface, teamMember: ErgoTeamMemberInterface }) => {
      return this._userRestConnectorService.removeUserErgoTeamMember(payloadData.user, payloadData.teamMember)
      .pipe(
        map((updatedUser: UserInterface) => new LoadSingleUserCompleted(updatedUser)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public deleteUser$: Observable<Action> = this._actions$
  .pipe(
    ofType(UserActionType.DeleteUser),
    map((action: DeleteUser) => action.payload),
    switchMap((userToDelete: UserInterface) => {
      return this._userRestConnectorService.deleteUser(userToDelete)
      .pipe(
        map((deletedUser: UserInterface) => new DeleteUserCompleted(deletedUser)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _authService: AuthService,
    private readonly _userService: UserService,
    private readonly _userRestConnectorService: UserRestConnectorService) { }

  /**
   * handleUserDetailResponse
   *
   * Handles the response returned from user detail request
   *
   * @param user Details for logged in user returned from user detail request
   *
   * @returns Action to forward to user reducer
  */
  private handleUserDetailResponse(user?: UserInterface): UserActions {
    // TODO: fully implement this once we have a better idea what the backend will return in various scenarios
    return user ? new UserUpdated(user) : new UserDetailRequestRejected('Received null user');
  }
}
